import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Server-side authorisation helper for every Creator Studio surface.
 * Reader accounts must never be able to reach writer-private data, even if
 * they call the RPC endpoint directly.
 */
async function assertWriter(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("profiles")
    .select("member_type")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data || data.member_type !== "writer") {
    throw new Error("Forbidden: Writer Mode is not active on this account");
  }
}

/** Turns the signed-in reader into a writer. Never creates a second account. */
export const activateWriterMode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { acceptedRules: boolean; penName?: string }) => {
    if (!input?.acceptedRules) throw new Error("You must accept the publishing rules");
    return {
      acceptedRules: true,
      penName: typeof input.penName === "string" ? input.penName.trim().slice(0, 60) : "",
    };
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { error: profileError } = await supabase
      .from("profiles")
      .update({ member_type: "writer" })
      .eq("id", userId);
    if (profileError) throw new Error(profileError.message);

    let penNameId: string | null = null;
    if (data.penName) {
      const { data: pen, error: penError } = await supabase
        .from("pen_names")
        .insert({ user_id: userId, name: data.penName, is_default: true })
        .select("id")
        .maybeSingle();
      if (penError) throw new Error(penError.message);
      penNameId = pen?.id ?? null;
    }

    const { error: writerError } = await supabase.from("writer_profiles").upsert(
      {
        user_id: userId,
        accepted_publishing_rules_at: new Date().toISOString(),
        ...(penNameId ? { default_pen_name_id: penNameId } : {}),
      },
      { onConflict: "user_id" },
    );
    if (writerError) throw new Error(writerError.message);

    return { memberType: "writer" as const };
  });

/** Aggregated Creator Studio overview. Writer-only, verified server-side. */
export const getStudioOverview = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    await assertWriter(supabase, userId);

    const [stories, followers, support] = await Promise.all([
      supabase
        .from("stories")
        .select("id, status, view_count, like_count, comment_count, bookmark_count")
        .eq("author_id", userId),
      supabase
        .from("follows")
        .select("*", { count: "exact", head: true })
        .eq("following_id", userId),
      supabase
        .from("creator_support")
        .select("amount_cents")
        .eq("recipient_id", userId)
        .eq("status", "succeeded"),
    ]);

    const rows = stories.data ?? [];
    const countBy = (status: string) => rows.filter((r) => r.status === status).length;

    return {
      published: countBy("published"),
      drafts: countBy("draft"),
      pending: countBy("submitted") + countBy("under_review"),
      changesRequested: countBy("changes_requested"),
      rejected: countBy("rejected"),
      approved: countBy("approved"),
      views: rows.reduce((n, r) => n + (r.view_count ?? 0), 0),
      likes: rows.reduce((n, r) => n + (r.like_count ?? 0), 0),
      comments: rows.reduce((n, r) => n + (r.comment_count ?? 0), 0),
      bookmarks: rows.reduce((n, r) => n + (r.bookmark_count ?? 0), 0),
      followers: followers.count ?? 0,
      supportCents: (support.data ?? []).reduce((n, r) => n + (r.amount_cents ?? 0), 0),
    };
  });

/** Every publication owned by the signed-in writer, with its workflow status. */
export const getMyPublications = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    await assertWriter(supabase, userId);

    const { data, error } = await supabase
      .from("stories")
      .select(
        "id, title, description, status, story_type, completion, word_count, view_count, like_count, comment_count, bookmark_count, updated_at, published_at, content_types(name, slug, is_personal), pen_names(name)",
      )
      .eq("author_id", userId)
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

/** Submission history plus moderation feedback for the signed-in writer. */
export const getMySubmissions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    await assertWriter(supabase, userId);

    const { data, error } = await supabase
      .from("submissions")
      .select(
        "id, status, submitted_at, reviewed_at, story_id, stories(title), moderation_decisions(id, decision, feedback, concerns, created_at)",
      )
      .eq("author_id", userId)
      .order("submitted_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

/** Support/tips received by the signed-in writer. Financial data is writer-private. */
export const getMySupport = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    await assertWriter(supabase, userId);

    const { data, error } = await supabase
      .from("creator_support")
      .select("id, amount_cents, currency, status, anonymous, message, created_at, stories(title)")
      .eq("recipient_id", userId)
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

/** Writer profile record (intro, pen name, support toggle). */
export const getWriterProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    await assertWriter(supabase, userId);

    const { data, error } = await supabase
      .from("writer_profiles")
      .select("id, intro, support_enabled, default_pen_name_id, accepted_publishing_rules_at")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  });

export const updateWriterProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { intro?: string; supportEnabled?: boolean; defaultPenNameId?: string | null }) => ({
    intro: typeof input.intro === "string" ? input.intro.trim().slice(0, 1000) : undefined,
    supportEnabled: typeof input.supportEnabled === "boolean" ? input.supportEnabled : undefined,
    defaultPenNameId: input.defaultPenNameId ?? undefined,
  }))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertWriter(supabase, userId);

    const patch: {
      intro?: string;
      support_enabled?: boolean;
      default_pen_name_id?: string | null;
    } = {};
    if (data.intro !== undefined) patch.intro = data.intro;
    if (data.supportEnabled !== undefined) patch.support_enabled = data.supportEnabled;
    if (data.defaultPenNameId !== undefined) patch.default_pen_name_id = data.defaultPenNameId || null;

    const { error } = await supabase
      .from("writer_profiles")
      .update(patch)
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
